import { ReferralAttribution } from '../model/dto';

const STORAGE_KEY = 'checkmate-referral-attribution';

const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30일
const CLICK_DEDUP_WINDOW_MS = 30 * 60 * 1000; // 30분

// 백엔드 ReferralClickReqDto의 @Size 제한과 동일 — 초과분을 그대로 보내면 서버가 거절하고
// 클라이언트는 에러를 삼키므로 조용히 유실된다. 캡처/읽기 시점에 미리 방어한다.
const MAX_CODE_LENGTH = 20;
const MAX_CHANNEL_LENGTH = 30;

// URL로 들어오는 값이라 신뢰 불가 — 초과 채널은 잘라서 기록 (코드와 달리 부분값도 유입 신호로 유효)
const sanitizeChannel = (channel: string | null): string | null =>
  channel !== null && channel.length > MAX_CHANNEL_LENGTH ? channel.slice(0, MAX_CHANNEL_LENGTH) : channel;

const readAttribution = (): ReferralAttribution | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const attribution = JSON.parse(raw) as ReferralAttribution;
    if (typeof attribution.code !== 'string' || typeof attribution.expiresAt !== 'number') {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    // 길이 제한 도입 전에 저장된 초과 길이 코드 — 서버에 유효할 수 없으므로 폐기
    if (attribution.code.length === 0 || attribution.code.length > MAX_CODE_LENGTH) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    if (Date.now() >= attribution.expiresAt) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return attribution;
  } catch {
    return null;
  }
};

/** 저장된 추천 코드 (만료됐으면 null). 가입 요청 첨부·결제 단계 자동 채움에 사용 */
export const getStoredReferralCode = (): string | null => readAttribution()?.code ?? null;

/**
 * ?ref= 링크 방문 캡처: 어트리뷰션을 30일 TTL로 저장하고, 이번 방문을 클릭으로 집계할지와
 * 집계에 사용할 채널값(길이 제한 반영)을 반환.
 * - 동일 코드로 30분 내 재방문(새로고침 등)은 중복 클릭으로 집계하지 않는다.
 * - 다른 코드가 적힌 링크는 기존 저장값을 덮어쓰고 새 클릭으로 집계한다.
 * - 20자 초과 코드는 유효할 수 없으므로 캡처 자체를 버린다.
 */
export const captureReferralVisit = (
  code: string,
  channel: string | null,
): { shouldRecordClick: boolean; channel: string | null } => {
  if (code.length === 0 || code.length > MAX_CODE_LENGTH) {
    return { shouldRecordClick: false, channel: null };
  }

  const safeChannel = sanitizeChannel(channel);
  const existing = readAttribution();
  const now = Date.now();

  const isDuplicateClick = existing?.code === code && now - existing.lastClickAt < CLICK_DEDUP_WINDOW_MS;

  const next: ReferralAttribution = {
    code,
    channel: safeChannel,
    expiresAt: now + ATTRIBUTION_TTL_MS,
    lastClickAt: isDuplicateClick ? existing.lastClickAt : now,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));

  return { shouldRecordClick: !isDuplicateClick, channel: safeChannel };
};
